import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { apiPath } from '../../lib/http';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const AVAILABILITY_TIMEOUT_MS = 45000;
const AVAILABILITY_RETRY_DELAY_MS = 10000;

const defaultWeeklyHours = () => DAY_LABELS.map((label, day) => ({
  day,
  label,
  enabled: day < 5,
  start: '09:00',
  end: day < 5 ? '17:00' : '13:00',
}));

const applyAvailability = (data, setters) => {
  if (!data || typeof data !== 'object') return;
  const slotDuration = Number(data.slot_duration_minutes);
  const minAdvanceHours = Number(data.min_advance_hours);
  const maxAdvanceDays = Number(data.max_advance_days);
  setters.setTimezone(data.timezone || 'UTC');
  setters.setSlotDuration(Number.isFinite(slotDuration) && slotDuration > 0 ? slotDuration : 30);
  setters.setMinAdvanceHours(Number.isFinite(minAdvanceHours) ? minAdvanceHours : 1);
  setters.setMaxAdvanceDays(Number.isFinite(maxAdvanceDays) && maxAdvanceDays > 0 ? maxAdvanceDays : 30);
  if (Array.isArray(data.weekly_hours) && data.weekly_hours.length) {
    setters.setWeeklyHours(data.weekly_hours.map((row, index) => ({
      day: Number(row.day ?? index),
      label: row.label || DAY_LABELS[Number(row.day ?? index)] || `Day ${index}`,
      enabled: Boolean(row.enabled),
      start: row.start || '09:00',
      end: row.end || '17:00',
    })));
  }
};

export const CrmAvailabilitySettings = ({ token }) => {
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [timezone, setTimezone] = useState('UTC');
  const [slotDuration, setSlotDuration] = useState(30);
  const [minAdvanceHours, setMinAdvanceHours] = useState(1);
  const [maxAdvanceDays, setMaxAdvanceDays] = useState(30);
  const [weeklyHours, setWeeklyHours] = useState(defaultWeeklyHours);

  useEffect(() => {
    if (!token) {
      setLoadError({ message: 'Sign in to load saved availability.', retryable: false });
      return undefined;
    }

    let cancelled = false;
    let timedOut = false;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, AVAILABILITY_TIMEOUT_MS);

    const load = async () => {
      setRefreshing(true);
      setLoadError(null);
      try {
        const res = await fetch(apiPath('/crm/appointments/availability'), {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
        if (cancelled) return;
        applyAvailability(data, {
          setTimezone,
          setSlotDuration,
          setMinAdvanceHours,
          setMaxAdvanceDays,
          setWeeklyHours,
        });
      } catch (err) {
        if (cancelled) return;
        if (err.name === 'AbortError') return;
        const message = timedOut
          ? 'Availability is still loading slowly. Defaults remain editable while we retry.'
          : (err.message || 'Failed to load availability');
        setLoadError({ message, retryable: timedOut });
        if (timedOut) {
          window.setTimeout(() => {
            if (!cancelled) setLoadAttempt((attempt) => attempt + 1);
          }, AVAILABILITY_RETRY_DELAY_MS);
        }
      } finally {
        window.clearTimeout(timer);
        if (!cancelled) setRefreshing(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [token, loadAttempt]);

  const retryLoad = () => {
    setLoadError(null);
    setLoadAttempt((attempt) => attempt + 1);
  };

  const updateDay = (day, patch) => {
    setWeeklyHours((prev) => prev.map((row) => (row.day === day ? { ...row, ...patch } : row)));
  };

  const save = async () => {
    if (!token) {
      toast.error('Sign in to save availability');
      return;
    }
    setSaving(true);
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), AVAILABILITY_TIMEOUT_MS);
    try {
      const res = await fetch(apiPath('/crm/appointments/availability'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          timezone,
          slot_duration_minutes: Number(slotDuration),
          min_advance_hours: Number(minAdvanceHours),
          max_advance_days: Number(maxAdvanceDays),
          weekly_hours: weeklyHours.map(({ day, enabled, start, end }) => ({ day, enabled, start, end })),
        }),
        signal: controller.signal,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      applyAvailability(data, {
        setTimezone,
        setSlotDuration,
        setMinAdvanceHours,
        setMaxAdvanceDays,
        setWeeklyHours,
      });
      setLoadError('');
      toast.success('Appointment availability saved');
    } catch (err) {
      toast.error(
        err.name === 'AbortError'
          ? 'Availability save timed out. Check your connection and retry.'
          : (err.message || 'Failed to save availability'),
      );
    } finally {
      window.clearTimeout(timer);
      setSaving(false);
    }
  };

  return (
    <section className="rounded-3xl border border-white/10 bg-zinc-900/60 p-5 space-y-4" data-testid="crm-availability-settings">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-white">Availability settings</h3>
          <p className="text-xs text-zinc-500 mt-1">
            Configure when customers can be booked. The booking modal uses these {slotDuration}-minute slots.
          </p>
          {refreshing ? (
            <p className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-zinc-500" data-testid="crm-availability-loading">
              <Loader2 className="w-3 h-3 animate-spin" /> Syncing saved settings…
            </p>
          ) : null}
          {loadError ? (
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-amber-300" data-testid="crm-availability-load-error">
              <span>{loadError.message}</span>
              {loadError.retryable ? (
                <button
                  type="button"
                  onClick={retryLoad}
                  className="inline-flex items-center gap-1 rounded-full border border-amber-300/30 px-2 py-0.5 text-amber-100 hover:bg-amber-300/10"
                  data-testid="crm-availability-retry-button"
                >
                  <RefreshCw className="h-3 w-3" /> Retry
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        <Button type="button" onClick={save} disabled={saving || !token} data-testid="crm-availability-save-button">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save availability
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
        <div>
          <label className="block text-zinc-400 mb-1">Slot length</label>
          <select
            value={slotDuration}
            onChange={(e) => setSlotDuration(Number(e.target.value))}
            className="w-full h-9 rounded-xl bg-zinc-950 border border-zinc-700 px-3 text-white"
            data-testid="crm-availability-slot-duration"
          >
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
          </select>
        </div>
        <div>
          <label className="block text-zinc-400 mb-1">Timezone</label>
          <input
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="UTC or America/New_York"
            className="w-full h-9 rounded-xl bg-zinc-950 border border-zinc-700 px-3 text-white"
            data-testid="crm-availability-timezone"
          />
        </div>
        <div>
          <label className="block text-zinc-400 mb-1">Min advance (hours)</label>
          <input
            type="number"
            min="0"
            value={minAdvanceHours}
            onChange={(e) => setMinAdvanceHours(Number(e.target.value))}
            className="w-full h-9 rounded-xl bg-zinc-950 border border-zinc-700 px-3 text-white"
            data-testid="crm-availability-min-advance"
          />
        </div>
        <div>
          <label className="block text-zinc-400 mb-1">Book up to (days)</label>
          <input
            type="number"
            min="1"
            value={maxAdvanceDays}
            onChange={(e) => setMaxAdvanceDays(Number(e.target.value))}
            className="w-full h-9 rounded-xl bg-zinc-950 border border-zinc-700 px-3 text-white"
            data-testid="crm-availability-max-advance"
          />
        </div>
      </div>

      <div className="space-y-2" data-testid="crm-availability-weekly-grid">
        {weeklyHours.map((row) => (
          <div key={row.day} className="grid grid-cols-[72px_72px_1fr_1fr] gap-2 items-center">
            <label className="inline-flex items-center gap-2 text-zinc-300">
              <input
                type="checkbox"
                checked={row.enabled}
                onChange={(e) => updateDay(row.day, { enabled: e.target.checked })}
                data-testid={`crm-availability-day-${row.day}-enabled`}
              />
              {row.label}
            </label>
            <span className="text-[10px] text-zinc-500">{row.enabled ? 'Open' : 'Closed'}</span>
            <input
              type="time"
              value={row.start}
              disabled={!row.enabled}
              onChange={(e) => updateDay(row.day, { start: e.target.value })}
              className="h-9 rounded-xl bg-zinc-950 border border-zinc-700 px-3 text-white disabled:opacity-40"
              data-testid={`crm-availability-day-${row.day}-start`}
            />
            <input
              type="time"
              value={row.end}
              disabled={!row.enabled}
              onChange={(e) => updateDay(row.day, { end: e.target.value })}
              className="h-9 rounded-xl bg-zinc-950 border border-zinc-700 px-3 text-white disabled:opacity-40"
              data-testid={`crm-availability-day-${row.day}-end`}
            />
          </div>
        ))}
      </div>
    </section>
  );
};

export default CrmAvailabilitySettings;
